import { ActivatedRoute } from "@angular/router";
import { Component, inject, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { Bill, BillingInfo, ContractType } from "src/app/shared/types/contract-types";
import { BillService } from "src/app/shared/services/bill.service";
import { ContractService } from "src/app/shared/services/contract.service";
import { PrintPreviewService } from "src/app/shared/services/print-preview.service";
import { FormBuilder, FormControl, FormGroup, ValidatorFn } from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import { DialogRef, DialogService } from "@ngneat/dialog";
import { showMessage } from "src/app/shared/utils/toast.popup";
import { PopupType } from "src/app/shared/enums/popup-types";

@Component({
  selector: "app-bill-view",
  templateUrl: "./bill-view.component.html",
  standalone: false,
})
export class BillViewComponent implements OnInit {
  billData!: Bill;
  total?: number;
  billId!: number;
  tax?: number;
  packageTitle!: string;
  contractData?: ContractType;
  form!: FormGroup;
  globalInfo!: BillingInfo;

  @ViewChild("modalTemplate") modalTemplate!: TemplateRef<unknown>;

  isLoading = false;
  isLoadingSend = false;
  isLoadingDownload = false;
  emails: string[] = [];
  plansToPay = 0;
  totalInRsd = 0;

  modal!: DialogRef;
  private dialog = inject(DialogService);

  constructor(
    private route: ActivatedRoute,
    private billService: BillService,
    private contractService: ContractService,
    private printPreviewService: PrintPreviewService,
    public translate: TranslateService,
    private fb: FormBuilder,
  ) {}

  ngOnInit() {
    this.route.paramMap.subscribe((param) => {
      const paramId = param.get("id");
      const paramContractId = param.get("contractId");
      const contractId = paramContractId ? Number.parseInt(paramContractId) : 0;
      const id = paramId ? Number.parseInt(paramId) : 0;
      this.billId = id;
      this.isLoading = true;
      this.loadBillingInfo();
      const subscription = this.contractService.getContractById(contractId).subscribe((res) => {
        subscription.unsubscribe();
        this.isLoading = false;
        this.contractData = res;
        this.packageTitle = res.package.title;

        const contractBill = res.bills?.find((el) => {
          return el.id === this.billId;
        });

        if (contractBill) {
          this.billData = {
            ...contractBill,
            id: this.billId,
          };

          this.tax = 0;
          this.totalInRsd = this.billData.price * (this.billData.exchangeRate ?? 1);

          const plansSubscription = this.billService.getNumberOfPlanToPay(this.billId).subscribe((res) => {
            plansSubscription.unsubscribe();
            this.plansToPay = res;
            this.total = this.billData.price + this.plansToPay * 2;
          });
        }
      });
    });

    this.form = this.fb.group({
      additionalEmails: new FormControl(this.emails, this.validators),
    });
  }

  public validators = [this.emailValidator as ValidatorFn];

  public emailValidator(control: FormControl) {
    const emailPattern = /^[^\s@]+@[^\s@][^\s.@]*\.[^\s@]+$/;
    if (!emailPattern.test(control.value)) {
      return {
        invalidEmail: true,
      };
    }
    return null;
  }

  public errorMessages = {
    invalidEmail: this.translate.instant("contractsendemail.email_invalid_format"),
  };

  loadBillingInfo() {
    const subscribe = this.billService.getBillingInformation().subscribe((res) => {
      subscribe.unsubscribe();
      this.globalInfo = res;
    });
  }

  onDownloadBill(): void {
    if (this.contractData?.id) {
      this.isLoadingDownload = true;
      const downloadSubscription = this.billService.downloadBill(this.contractData?.id, this.billId).subscribe({
        next: (response) => {
          downloadSubscription.unsubscribe();
          this.isLoadingDownload = false;
          const blob = new Blob([response], { type: "application/pdf" });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = `${this.contractData?.customer?.name}_${this.translate.instant("invoice")}_${this.billId}.pdf`;
          a.click();
          window.URL.revokeObjectURL(url);
        },
        error: (error) => {
          this.isLoadingDownload = false;
          console.error("Error downloading the file:", error);
        },
      });
    }
  }

  openSendModal() {
    if (this.contractData) {
      this.emails = [this.contractData?.email];
    }
    this.modal = this.dialog.open(this.modalTemplate, {
      windowClass: "modal-top",
      id: `dialog-${crypto.randomUUID()}`,
    });
  }

  sendBill() {
    if (this.emails.length === 0) {
      showMessage(PopupType.Warning, this.translate.instant("emails_are_missing_and_or_form_is_invalid"));
      return;
    }

    if (this.contractData) {
      this.isLoadingSend = true;
      const sendSubscription = this.billService.sendMailToCustomer(this.contractData?.id, this.billId, this.emails).subscribe({
        next: (res: string) => {
          sendSubscription.unsubscribe();
          this.isLoadingSend = false;
          showMessage(PopupType.Success, res || this.translate.instant("bills.success_mail_sent"));
          this.modal.close();
        },
        error: () => {
          sendSubscription.unsubscribe();
          this.isLoadingSend = false;
          showMessage(PopupType.Danger, this.translate.instant("bills.error_mail_failed"));
        },
      });
    }
  }

  printBill() {
    this.isLoading = true;
    const subscription = this.billService.generateBillHtml(this.billId).subscribe({
      next: (htmlContent) => {
        this.isLoading = false;
        this.printPreviewService.printFunctionality(htmlContent);
        subscription.unsubscribe();
      },
      complete: () => {
        subscription.unsubscribe();
        this.isLoading = false;
      },
    });
  }
}
